import { useId } from "react";
import { allowedStatesFor, isStateAllowed } from "../fields/ngStateLga.js";
import { getFieldDescriptor } from "../fields/registry.js";
import { FormattedText } from "./FormattedText.js";
import { StarRating } from "./StarRating.js";
import type { FieldRenderProps } from "./types.js";

/**
 * Renders one field. Type-dispatched but not one component per type: the 44 field types collapse
 * into a dozen input shapes, and a registry-driven switch keeps the public bundle small.
 *
 * Accessibility is not optional here (docs/01-PRODUCT-SPEC.md §13, WCAG 2.1 AA): every input has a
 * programmatic label, errors are associated by `aria-describedby` and marked `aria-invalid`, and
 * required fields carry `aria-required`.
 */

const inputBase =
  "w-full rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-2 disabled:opacity-60";

/** The <select>'s sentinel for "Other" — never itself stored; picking it just reveals the text
 *  input below, and whatever gets typed there becomes the field's real value. */
const OTHER_OPTION_VALUE = "__other__";

export const FieldRenderer = ({
  field,
  value,
  onChange,
  error,
  disabled,
  branding,
  lookups,
}: FieldRenderProps): JSX.Element | null => {
  const reactId = useId();
  const inputId = `${reactId}-${field.key}`;
  const errorId = `${inputId}-error`;
  const helpId = `${inputId}-help`;
  const descriptor = getFieldDescriptor(field.type);

  if (!descriptor) {
    return (
      <p role="alert" style={{ color: branding.error }} className="text-sm">
        Unknown field type “{field.type}”
      </p>
    );
  }

  const hints = descriptor.renderHints;

  // Identifiers and capture fields are never shown to an applicant at all.
  if (hints.isHiddenFromApplicant) return null;

  const config = field.config as Record<string, unknown>;
  const help = (config.helpText as string | undefined) ?? undefined;

  const describedBy = [error ? errorId : null, help ? helpId : null].filter(Boolean).join(" ");
  const shared = {
    id: inputId,
    disabled,
    "aria-required": field.isRequired || undefined,
    "aria-invalid": error ? true : undefined,
    "aria-describedby": describedBy || undefined,
    className: inputBase,
    style: {
      borderColor: error ? branding.error : branding.border,
      background: branding.surface,
      color: branding.text,
    },
  };

  // ── Layout-only types: content, not inputs ────────────────────────────────
  if (hints.isLayoutOnly) {
    switch (field.type) {
      case "heading": {
        const level = (config.level as number) ?? 2;
        const Tag = (level === 1 ? "h1" : level === 3 ? "h3" : "h2") as "h1" | "h2" | "h3";
        return (
          <Tag
            className={level === 1 ? "text-2xl font-semibold" : "text-lg font-semibold"}
            style={{ color: branding.primary, fontFamily: branding.headingFont }}
          >
            {field.label}
          </Tag>
        );
      }
      case "rich_text":
        // Parsed to elements by FormattedText, never injected as HTML. That keeps the property the
        // previous comment here was protecting — no injection path — while actually honouring the
        // author's paragraphs, headings, numbering and bold, which printing the raw string did not.
        return <FormattedText text={String(config.html ?? "")} className="text-sm" />;
      case "divider":
        return <hr style={{ borderColor: branding.border }} />;
      case "spacer": {
        const height = config.height === "large" ? 40 : config.height === "small" ? 8 : 20;
        return <div style={{ height }} aria-hidden="true" />;
      }
      case "page_break":
        return null; // pagination is the form's concern, not a rendered element
      case "image":
        return (
          <div
            className="flex items-center justify-center rounded-md border border-dashed p-6 text-xs"
            style={{ borderColor: branding.border, color: branding.text }}
          >
            {(config.altText as string) || "Image"}
          </div>
        );
      case "video_embed":
        return (
          <div
            className="flex items-center justify-center rounded-md border border-dashed p-6 text-xs"
            style={{ borderColor: branding.border, color: branding.text }}
          >
            {(config.caption as string) || "Video"}
          </div>
        );
      case "section":
        return null; // sections are containers, drawn by FormRenderer
      default:
        return null;
    }
  }

  const labelId = `${inputId}-label`;
  const label = (
    <label
      id={labelId}
      htmlFor={inputId}
      className="mb-1 block text-sm font-medium"
      style={{ color: branding.text }}
    >
      {field.label}
      {field.isRequired && (
        <span aria-hidden="true" style={{ color: branding.error }}>
          {" *"}
        </span>
      )}
    </label>
  );

  const messages = (
    <>
      {help && (
        <p id={helpId} className="mt-1 text-xs" style={{ color: branding.text, opacity: 0.7 }}>
          {help}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="mt-1 text-xs" style={{ color: branding.error }}>
          {error}
        </p>
      )}
    </>
  );

  const wrap = (control: JSX.Element): JSX.Element => (
    <div>
      {label}
      {control}
      {messages}
    </div>
  );

  const options = (config.options as { value: string; label: string }[] | undefined) ?? [];

  switch (field.type) {
    case "text_multi":
      return wrap(
        <textarea
          {...shared}
          rows={(config.rows as number) ?? 4}
          maxLength={config.maxLength as number | undefined}
          value={typeof value === "string" ? value : ""}
          onChange={(event) => onChange(event.target.value)}
        />,
      );

    case "rating": {
      const starCount = (config.starCount as number | undefined) ?? 5;
      return (
        <div>
          {label}
          <StarRating
            id={inputId}
            name={`${inputId}-stars`}
            starCount={starCount}
            allowHalf={config.allowHalf === true}
            value={typeof value === "number" ? value : null}
            onChange={onChange}
            disabled={disabled}
            required={field.isRequired}
            describedBy={describedBy}
            labelledBy={labelId}
            branding={branding}
          />
          {messages}
        </div>
      );
    }

    case "number":
    case "decimal":
    case "currency":
    case "slider":
    case "nps": {
      // The bounds are stated on the input, not only in the Zod schema. A `min` the browser
      // enforces is the difference between "-4" being impossible to type and being accepted here,
      // carried all the way to submit, and rejected there with an error the applicant has to
      // decode. `nps` is fixed at 0–10 by definition, so it does not read them from config.
      const isNps = field.type === "nps";
      const min = isNps ? 0 : (config.min as number | undefined);
      const max = isNps ? 10 : (config.max as number | undefined);
      return wrap(
        <input
          {...shared}
          type="number"
          inputMode={
            config.step === undefined || Number(config.step) % 1 === 0 ? "numeric" : "decimal"
          }
          min={min}
          max={max}
          step={(config.step as number | undefined) ?? undefined}
          value={typeof value === "number" ? value : ""}
          onChange={(event) =>
            onChange(event.target.value === "" ? null : Number(event.target.value))
          }
        />,
      );
    }

    case "email":
      return wrap(
        <input
          {...shared}
          type="email"
          autoComplete="email"
          value={typeof value === "string" ? value : ""}
          onChange={(event) => onChange(event.target.value)}
        />,
      );

    case "phone":
      return wrap(
        <input
          {...shared}
          type="tel"
          autoComplete="tel"
          value={typeof value === "string" ? value : ""}
          onChange={(event) => onChange(event.target.value)}
        />,
      );

    case "date":
    case "time":
    case "datetime":
    case "month_year":
      return wrap(
        <input
          {...shared}
          type={
            field.type === "month_year"
              ? "month"
              : field.type === "datetime"
                ? "datetime-local"
                : field.type
          }
          value={typeof value === "string" ? value : ""}
          onChange={(event) => onChange(event.target.value)}
        />,
      );

    case "dropdown":
    case "large_list": {
      // `allowOther` only ever appears in the dropdown descriptor's config (packages/shared/src/
      // fields/dropdown.ts) — undefined, and therefore false, for every other type that lands here.
      const allowOther = Boolean(config.allowOther);
      const stringValue = typeof value === "string" ? value : "";
      const matchesListedOption = options.some((option) => option.value === stringValue);
      // Derived from the value alone, not separate component state: a value that is non-empty and
      // not one of the listed options can only have gotten there via "Other" (or a prefill/import
      // carrying an answer our list has since dropped), so re-deriving it is what makes coming back
      // to a page, or loading a saved draft, show the custom answer correctly without extra wiring.
      const isOther = allowOther && stringValue !== "" && !matchesListedOption;

      return wrap(
        <>
          <select
            {...shared}
            value={isOther ? OTHER_OPTION_VALUE : stringValue}
            onChange={(event) => {
              if (event.target.value === OTHER_OPTION_VALUE) onChange("");
              else onChange(event.target.value);
            }}
          >
            <option value="">{(config.placeholder as string) ?? "Select…"}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
            {allowOther && <option value={OTHER_OPTION_VALUE}>Other (please specify)</option>}
          </select>
          {isOther && (
            <input
              {...shared}
              id={`${inputId}-other`}
              className={`${inputBase} mt-2`}
              type="text"
              autoFocus
              placeholder="Please specify"
              aria-label={`${field.label} — please specify`}
              value={stringValue}
              onChange={(event) => onChange(event.target.value)}
            />
          )}
        </>,
      );
    }

    case "radio":
    case "yes_no": {
      const radioOptions =
        field.type === "yes_no"
          ? [
              { value: "true", label: (config.yesLabel as string) ?? "Yes" },
              { value: "false", label: (config.noLabel as string) ?? "No" },
            ]
          : options;
      return (
        <fieldset aria-describedby={describedBy || undefined}>
          <legend className="mb-1 block text-sm font-medium" style={{ color: branding.text }}>
            {field.label}
            {field.isRequired && <span style={{ color: branding.error }}> *</span>}
          </legend>
          <div className="space-y-1">
            {radioOptions.map((option) => (
              <label key={option.value} className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name={inputId}
                  disabled={disabled}
                  checked={String(value) === option.value}
                  onChange={() =>
                    onChange(field.type === "yes_no" ? option.value === "true" : option.value)
                  }
                />
                {option.label}
              </label>
            ))}
          </div>
          {messages}
        </fieldset>
      );
    }

    case "multi_select":
    case "image_choice":
      return (
        <fieldset aria-describedby={describedBy || undefined}>
          <legend className="mb-1 block text-sm font-medium" style={{ color: branding.text }}>
            {field.label}
            {field.isRequired && <span style={{ color: branding.error }}> *</span>}
          </legend>
          <div className="space-y-1">
            {options.map((option) => {
              const selected = Array.isArray(value) ? (value as string[]) : [];
              return (
                <label key={option.value} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    disabled={disabled}
                    checked={selected.includes(option.value)}
                    onChange={(event) =>
                      onChange(
                        event.target.checked
                          ? [...selected, option.value]
                          : selected.filter((entry) => entry !== option.value),
                      )
                    }
                  />
                  {option.label}
                </label>
              );
            })}
          </div>
          {messages}
        </fieldset>
      );

    case "checkbox":
    case "decision_box":
      return (
        <div>
          <label className="flex items-start gap-2 text-sm">
            <input
              id={inputId}
              type="checkbox"
              disabled={disabled}
              aria-describedby={describedBy || undefined}
              aria-invalid={error ? true : undefined}
              checked={value === true}
              onChange={(event) => onChange(event.target.checked)}
            />
            <span>
              {(config.checkboxLabel as string) || (config.statement as string) || field.label}
              {field.isRequired && <span style={{ color: branding.error }}> *</span>}
            </span>
          </label>
          {messages}
        </div>
      );

    case "terms":
      return (
        <div>
          <div
            className="mb-2 max-h-72 overflow-y-auto overscroll-contain rounded-md border p-3 text-sm"
            style={{ borderColor: branding.border, background: branding.background }}
            tabIndex={0}
            role="region"
            aria-label={`${field.label} — terms text`}
          >
            <FormattedText text={(config.bodyText as string) || "Terms and conditions"} />
          </div>
          <label className="flex items-start gap-2 text-sm">
            <input
              id={inputId}
              type="checkbox"
              disabled={disabled}
              aria-required
              aria-invalid={error ? true : undefined}
              aria-describedby={describedBy || undefined}
              checked={value === true}
              onChange={(event) => onChange(event.target.checked)}
            />
            <span>
              {(config.acceptanceLabel as string) || "I accept"}
              <span style={{ color: branding.error }}> *</span>
            </span>
          </label>
          {messages}
        </div>
      );

    // ── Composites ──────────────────────────────────────────────────────────
    case "name": {
      const parts = (value ?? {}) as Record<string, string>;
      const setPart = (part: string, next: string): void => onChange({ ...parts, [part]: next });
      const showMiddle = config.showMiddleName !== false;
      const showTitle = config.showTitle === true;

      return (
        <fieldset>
          <legend className="mb-1 block text-sm font-medium" style={{ color: branding.text }}>
            {field.label}
            {field.isRequired && <span style={{ color: branding.error }}> *</span>}
          </legend>
          <div className="grid grid-cols-2 gap-2">
            {showTitle && (
              <input
                {...shared}
                id={`${inputId}-title`}
                aria-label="Title"
                placeholder="Title"
                value={parts.title ?? ""}
                onChange={(event) => setPart("title", event.target.value)}
              />
            )}
            <input
              {...shared}
              id={`${inputId}-first`}
              aria-label="First name"
              placeholder="First name"
              value={parts.firstName ?? ""}
              onChange={(event) => setPart("firstName", event.target.value)}
            />
            {showMiddle && (
              <input
                {...shared}
                id={`${inputId}-middle`}
                aria-label="Middle name"
                placeholder="Middle name"
                value={parts.middleName ?? ""}
                onChange={(event) => setPart("middleName", event.target.value)}
              />
            )}
            <input
              {...shared}
              id={`${inputId}-last`}
              aria-label="Last name"
              placeholder="Last name"
              value={parts.lastName ?? ""}
              onChange={(event) => setPart("lastName", event.target.value)}
            />
          </div>
          {messages}
        </fieldset>
      );
    }

    case "address": {
      const parts = (value ?? {}) as Record<string, string>;
      const setPart = (part: string, next: string): void => onChange({ ...parts, [part]: next });
      return (
        <fieldset>
          <legend className="mb-1 block text-sm font-medium" style={{ color: branding.text }}>
            {field.label}
            {field.isRequired && <span style={{ color: branding.error }}> *</span>}
          </legend>
          <div className="grid grid-cols-2 gap-2">
            {["street", "city", "lga", "state"].map((part) => (
              <input
                key={part}
                {...shared}
                id={`${inputId}-${part}`}
                aria-label={part}
                placeholder={part.charAt(0).toUpperCase() + part.slice(1)}
                value={parts[part] ?? ""}
                onChange={(event) => setPart(part, event.target.value)}
              />
            ))}
          </div>
          {messages}
        </fieldset>
      );
    }

    case "ng_state_lga": {
      const parts = (value ?? {}) as { state?: string; lga?: string };
      const allStates = lookups?.states ?? [];
      // The list the form was published with, not the JV's host pair: an Imo form offers Imo, and
      // a Scholarship form offers all thirty-seven. The validator applies the same list server
      // side, so a shortened dropdown is a convenience and never the only check.
      const allowed = allowedStatesFor(
        config as { allowedStates?: string[]; restrictToHostStates?: boolean },
      );
      const states = allowed
        ? allStates.filter((state) => isStateAllowed(state.name, allowed))
        : allStates;
      const lgas = parts.state ? (lookups?.lgasForState?.(parts.state) ?? []) : [];
      return (
        <fieldset>
          <legend className="mb-1 block text-sm font-medium" style={{ color: branding.text }}>
            {field.label}
            {field.isRequired && <span style={{ color: branding.error }}> *</span>}
          </legend>
          <div className="grid grid-cols-2 gap-2">
            <select
              {...shared}
              id={`${inputId}-state`}
              aria-label={(config.stateLabel as string) ?? "State"}
              value={parts.state ?? ""}
              onChange={(event) => onChange({ state: event.target.value, lga: "" })}
            >
              <option value="">{(config.stateLabel as string) ?? "State"}</option>
              {states.map((state) => (
                <option key={state.code} value={state.name}>
                  {state.name}
                </option>
              ))}
            </select>
            <select
              {...shared}
              id={`${inputId}-lga`}
              aria-label={(config.lgaLabel as string) ?? "LGA"}
              disabled={disabled || !parts.state}
              value={parts.lga ?? ""}
              onChange={(event) => onChange({ ...parts, lga: event.target.value })}
            >
              <option value="">{(config.lgaLabel as string) ?? "LGA"}</option>
              {lgas.map((lga) => (
                <option key={lga.id} value={lga.name}>
                  {lga.name}
                </option>
              ))}
            </select>
          </div>
          {messages}
        </fieldset>
      );
    }

    case "matrix_radio":
    case "matrix_checkbox": {
      const rows = (config.rows as { key: string; label: string }[] | undefined) ?? [];
      const columns = (config.columns as { value: string; label: string }[] | undefined) ?? [];
      const answers = (value ?? {}) as Record<string, unknown>;
      const isCheckbox = field.type === "matrix_checkbox";

      return (
        <fieldset>
          <legend className="mb-1 block text-sm font-medium" style={{ color: branding.text }}>
            {field.label}
            {field.isRequired && <span style={{ color: branding.error }}> *</span>}
          </legend>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th scope="col" className="p-2 text-left" />
                  {columns.map((column) => (
                    <th
                      key={column.value}
                      scope="col"
                      className="p-2 text-center text-xs font-medium"
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.key} style={{ borderTop: `1px solid ${branding.border}` }}>
                    <th scope="row" className="p-2 text-left text-xs font-normal">
                      {row.label}
                    </th>
                    {columns.map((column) => {
                      const rowValue = answers[row.key];
                      const checked = isCheckbox
                        ? Array.isArray(rowValue) && rowValue.includes(column.value)
                        : rowValue === column.value;
                      return (
                        <td key={column.value} className="p-2 text-center">
                          <input
                            type={isCheckbox ? "checkbox" : "radio"}
                            name={`${inputId}-${row.key}`}
                            disabled={disabled}
                            checked={checked}
                            aria-label={`${row.label}: ${column.label}`}
                            onChange={(event) => {
                              if (!isCheckbox) {
                                onChange({ ...answers, [row.key]: column.value });
                                return;
                              }
                              const current = Array.isArray(rowValue) ? (rowValue as string[]) : [];
                              onChange({
                                ...answers,
                                [row.key]: event.target.checked
                                  ? [...current, column.value]
                                  : current.filter((entry) => entry !== column.value),
                              });
                            }}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {messages}
        </fieldset>
      );
    }

    case "formula":
      return wrap(
        <input
          {...shared}
          readOnly
          value={typeof value === "number" ? String(value) : ""}
          aria-readonly="true"
        />,
      );

    // text_single, text_regex, website and anything else text-shaped
    default:
      return wrap(
        <input
          {...shared}
          type={field.type === "website" ? "url" : "text"}
          maxLength={config.maxLength as number | undefined}
          placeholder={(config.placeholderExample as string) ?? undefined}
          value={typeof value === "string" ? value : ""}
          onChange={(event) => onChange(event.target.value)}
        />,
      );
  }
};
